import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  Shield,
  Clock,
  Eye,
  Wrench,
  Home,
  FileText,
  Banknote,
  Truck,
  PartyPopper,
} from "lucide-react";
import { detailedSteps, companyInfo, heroImages } from "../data/mockData";

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const stepIcons = [FileText, Home, Banknote, FileText, Wrench, Truck, PartyPopper];

const SolutionPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FAF9F6] relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#C4956A]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-semibold uppercase tracking-wider text-[#C4956A]">
              Notre solution
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#1A1A2E] mt-4">
              Le déroulé complet de{" "}
              <span className="text-[#C4956A]">votre projet</span>
            </h1>
            <p className="text-lg text-[#1A1A2E]/60 mt-6 leading-relaxed">
              De la première rencontre à la livraison clé en main, découvrez les
              7 étapes qui structurent votre investissement avec Key Vest
              Massilia.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: "Gain de temps", desc: "Nous gérons tout de A à Z" },
              { icon: Eye, title: "Transparence", desc: "Reporting régulier photos/vidéos" },
              { icon: Shield, title: "Garantie décennale", desc: "Tous chantiers couverts" },
              { icon: Wrench, title: "Artisans qualifiés", desc: "Équipe professionnelle locale" },
            ].map((item, i) => (
              <AnimatedSection key={i}>
                <div className="flex items-start gap-4 p-6 bg-[#FAF9F6] rounded-xl">
                  <div className="w-12 h-12 rounded-xl bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#C4956A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1A1A2E]">{item.title}</h3>
                    <p className="text-sm text-[#1A1A2E]/50 mt-1">{item.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Steps */}
      <section className="py-24 bg-[#FAF9F6]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A2E]">
                Les 7 étapes de votre projet
              </h2>
            </div>
          </AnimatedSection>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-[#E8DDD3] hidden lg:block" />

            <div className="space-y-8">
              {detailedSteps.map((step, i) => {
                const StepIcon = stepIcons[i] || FileText;
                return (
                  <AnimatedSection key={i}>
                    <div className="relative flex gap-6 lg:gap-10">
                      <div className="flex-shrink-0 relative z-10">
                        <div className="w-16 h-16 rounded-2xl bg-[#C4956A] flex items-center justify-center shadow-lg shadow-[#C4956A]/20">
                          <StepIcon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 pb-8">
                        <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm border border-[#E8DDD3]/50">
                          <div className="flex items-center gap-3 mb-4">
                            <span className="text-sm font-bold text-[#C4956A] bg-[#C4956A]/10 px-3 py-1 rounded-full">
                              {"Étape"} {step.step}
                            </span>
                            <h3 className="text-xl font-bold text-[#1A1A2E]">
                              {step.title}
                            </h3>
                          </div>
                          <ul className="space-y-3">
                            {step.items.map((item, j) => (
                              <li key={j} className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-[#C4956A] flex-shrink-0 mt-0.5" />
                                <span className="text-sm text-[#1A1A2E]/70">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                          {step.payment && (
                            <div className="mt-4 pt-4 border-t border-[#E8DDD3]/50">
                              <p className="text-xs font-semibold text-[#C4956A] uppercase tracking-wider">
                                {step.payment}
                              </p>
                            </div>
                          )}
                          {step.note && (
                            <div className="mt-4 pt-4 border-t border-[#E8DDD3]/50">
                              <p className="text-xs text-[#1A1A2E]/50 italic">
                                {step.note}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </AnimatedSection>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Before/After */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1A2E]">
                Le résultat : votre bien{" "}
                <span className="text-[#C4956A]">clé en main</span>
              </h2>
              <p className="text-[#1A1A2E]/60 mt-4">
                Rénovation haut de gamme, ameublement soigné et bien prêt à louer.
              </p>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="relative rounded-2xl overflow-hidden h-80">
                <img
                  src={heroImages.main}
                  alt="Rénovation"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg">
                  <p className="text-sm font-semibold text-[#1A1A2E]">{"Après rénovation"}</p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden h-80">
                <img
                  src={heroImages.secondary}
                  alt="Décoration"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg">
                  <p className="text-sm font-semibold text-[#1A1A2E]">{"Aménagement intérieur"}</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Referral Banner */}
      <section className="py-16 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="bg-[#C4956A]/10 border border-[#C4956A]/20 rounded-2xl p-8 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl font-bold text-[#1A1A2E]">
                  Programme de parrainage
                </h3>
                <p className="text-[#1A1A2E]/60 mt-2">
                  Vous nous recommandez ? Si un projet est signé, nous vous
                  reversons <span className="font-bold text-[#C4956A]">5%</span>{" "}
                  de nos honoraires en remerciement.
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all duration-300 flex-shrink-0"
              >
                En savoir plus
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-white">
                {"Prêt à lancer"}{" "}
                <span className="text-[#C4956A]">votre projet</span> ?
              </h2>
              <p className="text-white/60 max-w-md mx-auto">
                Contactez-nous pour un premier échange gratuit et sans engagement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C4956A] text-white font-medium rounded-lg hover:bg-[#B38459] transition-all duration-300"
                >
                  Prendre rendez-vous
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 transition-all duration-300"
                >
                  <Phone className="w-4 h-4" />
                  {companyInfo.phone}
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default SolutionPage;
